/**
 * Node Mappers Module
 *
 * This module converts business data (pipelineItems) into React Flow Node objects.
 * It handles the creation of all node types: resources, flows, credentials, etc.
 *
 * Key Functions:
 * - mapToNodes: Main entry point - converts pipelineItems to nodes
 * - handleFlowNode: Creates flow container nodes
 * - handleResourceNode: Creates resource nodes (sources, datasets, sinks)
 * - handleRetrieverFlows: Handles RAG retriever flow nodes
 *
 * Node Creation Flow:
 * 1. Organize pipeline items (organizeFlowItems)
 * 2. For each item, determine node type
 * 3. Call appropriate handler (handleFlowNode, handleResourceNode)
 * 4. Build node using nodeBuilders
 * 5. Handle children recursively
 * 6. Handle special cases (credentials, references, etc.)
 *
 * Node Types Created:
 * - Flow nodes: Container nodes that hold other nodes
 * - Resource nodes: Sources, datasets, sinks
 * - Credential nodes: Credential badges attached to sources
 * - Nexset loader nodes: Loaders for pending nexsets
 * - Reference nodes: References to other resources
 *
 * @see docs/FlowNodesArchitecture.md - Node creation section
 * @see docs/FlowNodesModificationGuide.md - How to add new node types
 */

import { Node } from '@xyflow/react';
import {
  createChildrenReducer,
  hasChildren,
  hasDescendantSink,
  shouldCreateNexsetLoader,
} from './childrenHandlers';
import { HANDLE_STATUS, SPECIAL_NODES } from './constants';
import { NODE_WIDTH } from './consts';
import {
  buildByoDestShellNode,
  buildByoSourceShellNode,
  buildCodeNode,
  buildCredentialNode,
  buildFlowNode,
  buildNexsetLoaderNode,
  buildResourceNode,
} from './nodeBuilders';
import { FLOW_TYPE_CUSTOM } from 'js/constants/pipelineConstants';
import { organizeFlowItems } from './nodeRenderers';
import { createReferenceBuilder, shouldShowBottomHandle } from './referenceHandlers';
import type { CreateNodesFunction, MapToNodesOptions } from './types';
import {
  filterWebhookResponseSourcesFromItems,
  isPipelineSourceItem,
  shouldRenderNode,
} from './utils';

/**
 * Helper to consistently derive an item's key.
 *
 * Items can have either flow_node_id or id. This function provides
 * a consistent way to get the key for lookups and comparisons.
 */
function getItemKey(item: { flow_node_id?: string; id?: string }): string {
  return (item?.flow_node_id ?? item?.id) as string;
}

function handleRetrieverFlows(
  retrieverFlows,
  { nodes, item, entities, options, nodeProps, expandedNodes },
) {
  retrieverFlows.forEach((items: any) => {
    mapToNodes(nodes, {
      pipelineItems: items,
      parentId: `node-${item.flow_node_id}`,
      entities,
      flowsOrder: options.flowsOrder,
      keepFlowsOrder: options.keepFlowsOrder,
      isRagFlow: options.isRagFlow,
      aclQueryMode: options.aclQueryMode,
      nodeProps: {
        ...nodeProps,
        node_type: item.node_type,
        expandedNodes,
      },
    });
  });
}

type HandleFlowNodeOptions = {
  item: any;
  nodes: Node[];
  parentId: string | undefined;
  options: MapToNodesOptions;
  isSecondaryFlow: boolean;
};

function handleFlowNode({
  item,
  nodes,
  parentId,
  options,
  isSecondaryFlow,
}: HandleFlowNodeOptions) {
  const node = buildFlowNode(item, {
    parentId,
    pipelineItems: options.pipelineItems,
    nodeProps: options.nodeProps,
    aclQueryMode: options.aclQueryMode,
    additionalNodeProps: {},
    isSecondaryFlow,
  });

  nodes.push(node);

  if (item.items) {
    mapToNodes(nodes, {
      pipelineItems: item.items,
      entities: options.entities,
      aclQueryMode: options.aclQueryMode,
      isRagFlow: options.isRagFlow,
      nodeProps: {
        ...options.nodeProps,
        linksMap: options.linksMap,
        flow: item,
      },
      isSecondaryFlow,
      parentId: node.id,
    });
  }

  // Update flow dimensions
  node.data.width = NODE_WIDTH;

  // Handle triggering flows
  if (item.triggeringFlowsMap) {
    mapToNodes(nodes, {
      ...options,
      pipelineItems: item.triggeringFlowsMap,
      nodeProps: {
        ...options.nodeProps,
        linksMap: options.linksMap,
        flow_triggers: item.flow_triggers,
      },
    });
  }
}

type HandleResourceNodeOptions = {
  item: any;
  nodes: Node[];
  parentId: string | undefined;
  options: MapToNodesOptions;
  createNodes: CreateNodesFunction;
  isSecondaryFlow: boolean;
  isApiServerFlow: boolean;
  isSqlAutomation?: boolean;
  additionalNodeProps?: Record<string, any>;
};

function handleResourceNode({
  item,
  nodes,
  parentId,
  options,
  createNodes,
  isSecondaryFlow,
  isApiServerFlow,
  isSqlAutomation,
  additionalNodeProps = {},
}: HandleResourceNodeOptions) {
  const { pipelineItems, entities, aclQueryMode, isRagFlow, nodeProps } = options;

  // BYO (custom) flows: the Code node serves as the data-flow node instead of
  // a nexset. Skip rendering the DATA_SET that belongs to a custom-flow source
  // so the NEXSET card doesn't appear on the canvas alongside the Code node.
  //
  // NOTE: createChildrenReducer passes the GRANDPARENT's id as parentId, so we
  // cannot rely on `parentId` here. Instead, scan pipelineItems for a custom
  // source whose `children` array includes this DATA_SET key.
  if (item.type === 'DATA_SET' && nodeProps?.byoV2Enabled) {
    const dsKey = String(item.flow_node_id ?? item.id ?? '');
    const isChildOfCustomSource = Object.values(pipelineItems as any).some(
      (p: any) =>
        p?.type === 'SOURCE' &&
        p?.flow_type === FLOW_TYPE_CUSTOM &&
        (p?.children ?? []).some((c: any) => String(c) === dsKey),
    );
    if (isChildOfCustomSource) return;
  }

  // BYO create flow: render a dashed SOURCE shell + CODE shell + DEST shell
  // before any real source/sink is configured.
  if (item.type === 'SOURCE' && item.isByoSourceShell === true && nodeProps?.byoV2Enabled) {
    nodes.push(buildByoSourceShellNode(item, { parentId, nodeProps, aclQueryMode }));
    nodes.push(buildCodeNode(item, { parentId, nodeProps, aclQueryMode }));
    nodes.push(buildByoDestShellNode(item, { parentId, nodeProps, aclQueryMode }));
    return;
  }

  const { expandedNodes, expandedSources } = nodeProps;
  const expanded = expandedNodes.includes(item.flow_node_id);
  const expandedSource = expandedSources[item.flow_node_id];

  // Build main resource node
  const node = buildResourceNode(item, {
    pipelineItems,
    nodeProps,
    aclQueryMode,
    parentId,
    expanded,
    expandedSource,
    additionalNodeProps: {
      isSecondaryFlow,
      bottomHandleStatus: shouldShowBottomHandle(item, entities) ? HANDLE_STATUS.RELATION : '',
      ...additionalNodeProps,
    },
  });

  const isNotPreview = !nodeProps?.isPreview;

  // Create nexset loader if needed
  if (shouldCreateNexsetLoader(item, isApiServerFlow, isSqlAutomation) && isNotPreview) {
    const loaderNode = buildNexsetLoaderNode(item, {
      parentId,
      nodeProps,
      isSqlAutomation,
    });
    nodes.push(loaderNode);
  }

  nodes.push(node);

  /**
   * @Handle: BYO (custom) flow Code node
   *
   * BYO flows have no detected nexset. Render a synthetic "Code" node right
   * after the source so the canvas reads Source → Code → Destination, with the
   * edit-code / set-up-destination / run-now actions living on the Code node
   * (the nexset's role). createEdges reroutes the source→sink edge through it.
   */
  if (
    item.type === 'SOURCE' &&
    item.flow_type === FLOW_TYPE_CUSTOM &&
    nodeProps?.byoV2Enabled &&
    !nodes.some((n) => n.id === 'code-' + item.flow_node_id)
  ) {
    nodes.push(buildCodeNode(item, { parentId, nodeProps, aclQueryMode }));

    // Add destination shell when the flow has no sink yet so it renders as an
    // independent draggable node instead of being embedded inside CodeNode.
    // Scoped to THIS source's subtree — pipelineItems holds every flow on a
    // multi-flow canvas, and another flow's sink must not hide this shell.
    const hasSink = hasDescendantSink(item, pipelineItems as any);
    if (!hasSink && !nodes.some((n) => n.id === 'byo-dest-shell-' + item.flow_node_id)) {
      nodes.push(buildByoDestShellNode(item, { parentId, nodeProps, aclQueryMode }));
    }
  }

  /**
   * @Handle: Shared Dataset Node Creation
   */
  if (item.shared_data_set_id && isNotPreview) {
    const sharedDataSet = entities?.dataSets?.[item.shared_data_set_id];

    if (!sharedDataSet) return;

    const isPresentInPipelineItems = Object.values(pipelineItems).some(
      (p) => p.id === sharedDataSet.id,
    );

    if (isPresentInPipelineItems) return;

    if (nodes.some((n) => n.id === item.shared_data_set_id)) return;

    const sharedDataSetItem = {
      ...sharedDataSet,
      flow_node_id: item.shared_data_set_id,
      id: item.shared_data_set_id,
      origin_node_id: item.shared_data_set_id,
      type: 'DATA_SET',
      node_type: 'data_set',
      dataSetType: 'shared',
      isReference: true,
      linked_flows: [],
      flow_triggers: [],
      children: [item.flow_node_id], // we must add created dataset as child to ensure "right" handle is created
    };

    const sharedDataSetNode = buildResourceNode(sharedDataSetItem, {
      pipelineItems,
      nodeProps,
      aclQueryMode,
      parentId,
      expanded: false,
      expandedSource: undefined,
      additionalNodeProps: {
        isSecondaryFlow,
      },
    });

    nodes.push(sharedDataSetNode);
  }

  // Create credential node if needed (must match createCredentialEdge source-like rules).
  //
  // The `!parentId || expandedNodes.includes(parentId)` guard used to also gate
  // credential creation on the parent flow container being expanded. That caused
  // a first-paint regression for flows wrapped in a container (dependent /
  // linked flows): the credential node wasn't created until auto-expand fired,
  // which in turn meant the credential → source edge was dropped by
  // `createCredentialEdge`'s `existingNodeIds` guard — so the edge only
  // appeared after the user toggled layout direction. Credential nodes render
  // at canvas top-level (React Flow ignores their parentId for positioning
  // since we overwrite `position` in dagreLayout), so the expansion gate was
  // strictly a bug and is dropped here.
  if (
    isPipelineSourceItem(item) &&
    item?.node_type !== 'api_target' &&
    (item?.data_credentials_id || item?.data_credentials_group_id) &&
    isNotPreview
  ) {
    const credentialNode = buildCredentialNode(item, {
      parentId,
      entities,
      nodeProps,
      additionalNodeProps: {},
      isRagFlow,
      aclQueryMode,
      pipelineItems,
    });

    if (credentialNode) {
      nodes.push(credentialNode);
    }
  }

  if (item.node_type === SPECIAL_NODES.DATA_RETRIEVER_HOLDER && !item.retriever_flows?.length) {
  } else if (item.retriever_flows) {
    // Handle retriever flows
    handleRetrieverFlows(item.retriever_flows, {
      nodes,
      item,
      entities,
      options,
      nodeProps,
      expandedNodes,
    });
  } else {
    const referenceBuilder = createReferenceBuilder(item, nodes, {
      entities,
      parentId,
    });
    referenceBuilder?.(createNodes);
  }

  // Handle children
  if (hasChildren(item)) {
    // If the parent has passed down a specific Y position for its children, use that.
    // Otherwise, calculate it based on the current node's position.
    const columnY = 0;

    const childrenReducer = createChildrenReducer(item, nodes, {
      parentId,
      expanded,
      expandedSources,
      pipelineItems,
      isRagFlow,
      columnY,
    });

    return childrenReducer(createNodes);
  }
}

export function mapToNodes(nodes: Node[], options: MapToNodesOptions) {
  const {
    pipelineItems,
    keepFlowsOrder = false,
    flowsOrder = undefined,
    parentId = undefined,
  } = options;

  const filteredPipelineItems = filterWebhookResponseSourcesFromItems(pipelineItems);
  const nodeMap = { ...filteredPipelineItems };

  const createNodes: CreateNodesFunction = (nodes, item, parentId, additionalNodeProps = {}) => {
    // Skip rendering if conditions not met
    if (!shouldRenderNode(item, nodeMap)) {
      return;
    }

    // Handle different node types using specific functions
    if (item.resource_type === 'flow') {
      delete nodeMap[getItemKey(item)];
      return handleFlowNode({
        item,
        nodes,
        parentId,
        options,
        isSecondaryFlow: options.isSecondaryFlow,
      });
    }

    // Handle resource nodes
    delete nodeMap[getItemKey(item)];
    return handleResourceNode({
      item,
      nodes,
      parentId,
      options,
      createNodes,
      isSecondaryFlow: options.isSecondaryFlow,
      isApiServerFlow: options.isApiServerFlow,
      isSqlAutomation: options.isSqlAutomation,
      additionalNodeProps,
    });
  };

  // Use specific function for organizing and creating resource nodes
  const items = organizeFlowItems(filteredPipelineItems, keepFlowsOrder || false, flowsOrder);

  items.forEach((item) => {
    createNodes(nodes, item, parentId);
  });
}
